import type Phaser from 'phaser';
import { createGame } from '../game/createGame';
import { DAYS, getDay } from '../data/days';
import { MENU_ITEMS } from '../data/menu';
import { UPGRADES, upgradePrice } from '../data/upgrades';
import { gameEvents } from '../game/events';
import { AudioSystem } from '../game/systems/AudioSystem';
import { SaveSystem } from '../game/systems/SaveSystem';
import type { CampaignStats, DayResult, FinaleState, HudState, SaveData, UpgradeId } from '../game/types';

type PresentationState = 'MENU' | 'DAY_BRIEFING' | 'GAMEPLAY' | 'PAUSED' | 'DAY_RESULT' | 'UPGRADE_FROM_MENU' | 'UPGRADE_FROM_RESULT' | 'GAME_COMPLETE';

export class GameApp {
  private readonly save = new SaveSystem();
  private readonly audio = new AudioSystem(this.save.snapshot.settings);
  private game?: Phaser.Game;
  private currentResult?: DayResult;
  private devSpeed = 1;
  private devHitboxes = false;
  private hintTimeout?: number;
  private selectedDay = 1;
  private presentationState: PresentationState = 'MENU';

  constructor(private readonly root: HTMLElement) {
    this.render();
    this.bindEvents();
    this.showScreen('menu');
    this.setPresentationState('MENU');
  }

  private render(): void {
    const snapshot = this.save.snapshot;
    this.root.innerHTML = `
      <main class="app-shell">
        <section class="screen menu-screen is-active" data-screen="menu" aria-label="Ana menü">
          <div class="menu-backdrop" aria-hidden="true"></div>
          <div class="menu-vignette" aria-hidden="true"></div>
          <header class="brand-lockup">
            <span class="brand-kicker">YASEMİN &amp; FEVZİ</span>
            <h1>Little Corner</h1>
            <p class="brand-flourish"><span></span> küçük kafe, güzel telaş <span></span></p>
          </header>
          <nav class="menu-card" aria-label="Oyun menüsü">
            <button class="primary-menu-button" data-action="new-game"><span class="button-icon">▶</span> OYNA</button>
            <button data-action="continue" ${Object.keys(snapshot.bestResults).length === 0 ? 'disabled' : ''}><span class="button-icon">↻</span> DEVAM ET</button>
            <button data-action="day-select"><span class="button-icon">☀</span> GÜN SEÇ</button>
            <button data-action="cafe"><span class="button-icon">✦</span> KAFEM</button>
            <button data-action="settings"><span class="button-icon">⚙</span> AYARLAR</button>
            <div class="save-note"><span></span> İlerleme bu cihazda saklanır</div>
          </nav>
          <div class="menu-stamp" aria-hidden="true">EST. 2026<br><strong>LC</strong></div>
        </section>

        <section class="screen game-screen" data-screen="game" aria-label="Kafe">
          <div class="game-frame">
            <div id="phaser-root" class="phaser-root"></div>
            <header class="hud" aria-live="polite">
              <div class="hud-pill day-pill"><span>GÜN</span><strong id="hud-day">1</strong></div>
              <div class="hud-pill money-pill"><span>KAZANÇ</span><strong id="hud-earnings">₺0</strong></div>
              <div class="goal-card">
                <div class="goal-label"><span>HEDEF</span><strong id="hud-goal">₺70</strong></div>
                <div class="goal-track"><i id="goal-progress"></i></div>
              </div>
              <div class="hud-pill combo-pill" id="combo-pill"><span>KOMBO</span><strong id="hud-combo">×0</strong></div>
              <button class="round-button" data-action="pause" aria-label="Oyunu duraklat">Ⅱ</button>
            </header>
            <div class="game-hint" id="game-hint"><span class="hint-spark">✦</span><span>Kapıdaki ilk misafiri karşıla</span></div>
            <div class="finale-caption" id="finale-caption" hidden><small>SON SERVİS</small><strong></strong></div>
            <div class="toast-stack" id="toast-stack" aria-live="polite"></div>
          </div>
          <aside class="dev-panel" id="dev-panel" hidden>
            <div class="dev-title">AYAR MASASI <span>DEV</span></div>
            <div class="dev-actions">
              <button data-dev="money">Gün +₺25</button><button data-dev="cafe-money">Kafe +₺500</button>
              <button data-dev="spawn">Müşteri</button><button data-dev="unlock">Günleri aç</button>
              <button data-dev="patience">Sabır 8sn</button><button data-dev="ready">Hazırla</button>
              <button data-dev="speed">Hız ×1</button><button data-dev="inspect">Durum</button>
              <button data-dev="gorkem">Görkem</button><button data-dev="fevzi">Fevzi aç/kapat</button>
              <button data-dev="helper">Fevzi eylem</button><button data-dev="reset-upgrades">Yükseltmeleri sıfırla</button>
              <button data-dev="finale">G20 finali</button>
              <button data-dev="hitboxes">HITBOXLARI GÖSTER</button>
              <div class="dev-days">${DAYS.map((day) => `<button data-dev-day="${day.id}">G${day.id}</button>`).join('')}</div>
              <button data-dev="reset" class="danger">Kaydı sıfırla</button>
            </div>
            <pre id="dev-output">Hazır.</pre>
          </aside>
        </section>

        <div class="modal-layer" id="modal-layer" hidden>
          <section class="modal day-start-modal" data-modal="day-start" hidden>
            <div class="modal-emblem">☕</div>
            <p class="eyebrow">KÖŞE KAFE · SABAH 08:00</p>
            <h2 id="day-start-title">GÜN 1</h2>
            <p class="day-subtitle" id="day-start-subtitle">İlk Servis</p>
            <div class="target-banner"><span>HEDEF</span><strong id="day-start-goal">₺70</strong></div>
            <div class="challenge-line"><span>✦ BONUS</span> <b id="day-start-challenge">Hiç müşteri kaybetme</b></div>
            <div class="menu-preview" id="day-start-menu"><span>BUGÜNÜN MENÜSÜ</span><b>☕ Kahve</b><b>🍰 Pasta</b></div>
            <button class="modal-primary" data-action="start-day">GÜNE BAŞLA</button>
            <button class="text-button" data-action="close-modal">Menüye dön</button>
          </section>

          <section class="modal day-complete-modal" data-modal="day-complete" hidden>
            <div class="result-rays" aria-hidden="true"></div>
            <div class="modal-emblem success">✓</div>
            <p class="eyebrow" id="result-eyebrow">HEDEF TAMAMLANDI</p>
            <h2 id="result-title">GÜN TAMAMLANDI</h2>
            <div class="stars" id="result-stars">★ ★ ☆</div>
            <div class="result-grid">
              <div><span>KAZANÇ</span><strong id="result-earnings">₺0</strong></div>
              <div><span>BAHŞİŞ</span><strong id="result-tips">₺0</strong></div>
              <div><span>SERVİS</span><strong id="result-served">0</strong></div>
              <div><span>KAYIP</span><strong id="result-lost">0</strong></div>
              <div><span>EN İYİ KOMBO</span><strong id="result-combo">×0</strong></div>
              <div><span>KAFE PARASI</span><strong id="result-wallet">₺0</strong></div>
            </div>
            <div class="bonus-result" id="result-bonus">✦ Kusursuz Gün <strong>+₺12</strong></div>
            <div class="result-actions">
              <button class="modal-primary" id="result-next" data-action="next-day">SONRAKİ GÜN</button>
              <button data-action="cafe">KAFEYİ GELİŞTİR</button>
              <button class="text-button" data-action="replay">TEKRAR OYNA</button>
            </div>
          </section>

          <section class="modal compact-modal" data-modal="settings" hidden>
            <div class="modal-topline"><div><p class="eyebrow">KAFE AYARLARI</p><h2>AYARLAR</h2></div><button class="close-button" data-action="close-modal" aria-label="Kapat">×</button></div>
            ${this.settingSlider('master', 'ANA SES', snapshot.settings.master)}
            ${this.settingSlider('music', 'MÜZİK', snapshot.settings.music)}
            ${this.settingSlider('effects', 'EFEKTLER', snapshot.settings.effects)}
            <div class="cheat-code-row">
              <label for="cheat-code">HİLE KODU</label>
              <div><input id="cheat-code" type="password" autocomplete="off" spellcheck="false" placeholder="Kodu gir"><button data-action="apply-cheat">UYGULA</button></div>
              <small id="cheat-code-status" aria-live="polite"></small>
            </div>
            <button class="modal-primary" data-action="save-settings">KAYDET</button>
          </section>

          <section class="modal day-select-modal" data-modal="day-select" hidden>
            <div class="modal-topline"><div><p class="eyebrow">SERVİS TAKVİMİ</p><h2>GÜN SEÇ</h2></div><button class="close-button" data-action="close-modal" aria-label="Kapat">×</button></div>
            <div class="day-grid">
              ${this.renderDayCards(snapshot)}
            </div>
          </section>

          <section class="modal cafe-modal" data-modal="cafe" hidden>
            <div class="modal-topline"><div><p class="eyebrow">KÜÇÜK KÖŞEN</p><h2>KAFEM</h2></div><button class="close-button" data-action="close-modal" aria-label="Kapat">×</button></div>
            <div class="wallet">KAFE PARASI <strong id="cafe-wallet">₺${snapshot.cafeMoney}</strong></div>
            <p class="cafe-copy">Kazancını küçük ama kalıcı geliştirmelere ayır.</p>
            <div class="upgrade-shop" id="upgrade-shop">${this.renderUpgradeCards(snapshot)}</div>
          </section>

          <section class="modal compact-modal pause-modal" data-modal="pause" hidden>
            <div class="modal-emblem">Ⅱ</div><p class="eyebrow">KISA BİR MOLA</p><h2>OYUN DURAKLATILDI</h2>
            <button class="modal-primary" data-action="resume">DEVAM ET</button>
            <button data-action="settings">AYARLAR</button>
            <button class="text-button" data-action="main-menu">ANA MENÜ</button>
          </section>

          <section class="modal compact-modal" data-modal="notice" hidden>
            <div class="modal-emblem">✦</div><p class="eyebrow">YENİ SAYFA</p><h2 id="notice-title">ÇOK YAKINDA</h2>
            <p id="notice-copy" class="notice-copy">Day 1 dikey diliminin ardından açılacak.</p>
            <button class="modal-primary" data-action="close-modal">TAMAM</button>
          </section>

          <section class="modal campaign-complete-modal" data-modal="campaign-complete" hidden>
            <div class="completion-glow" aria-hidden="true">✦</div>
            <p class="eyebrow">YASEMİN &amp; FEVZİ · LITTLE CORNER</p>
            <h2>20 GÜN TAMAMLANDI</h2>
            <p class="completion-quote">“En güzel köşe, birlikte emek verdiğin yerdir.”</p>
            <div class="result-grid campaign-stats">
              <div><span>SERVİS</span><strong id="campaign-served">0</strong></div>
              <div><span>TOPLAM KAZANÇ</span><strong id="campaign-earned">₺0</strong></div>
              <div><span>BAHŞİŞ</span><strong id="campaign-tips">₺0</strong></div>
              <div><span>EN İYİ KOMBO</span><strong id="campaign-combo">×0</strong></div>
              <div><span>YILDIZ</span><strong id="campaign-stars">0</strong></div>
              <div><span>GELİŞTİRME</span><strong id="campaign-upgrades">0</strong></div>
            </div>
            <button class="modal-primary" data-action="main-menu">ANA MENÜYE DÖN</button>
            <button class="text-button" data-action="day-select">AÇIK GÜNLERİ TEKRAR OYNA</button>
          </section>
        </div>
      </main>
    `;
  }

  private settingSlider(id: keyof SaveData['settings'], label: string, value: number): string {
    return `<label class="setting-row" for="setting-${id}"><span>${label}</span><input id="setting-${id}" data-setting="${id}" type="range" min="0" max="100" value="${Math.round(value * 100)}"><output>${Math.round(value * 100)}%</output></label>`;
  }

  private renderDayCards(snapshot: SaveData): string {
    const card = (day: (typeof DAYS)[number]): string => {
      const unlocked = day.id <= snapshot.unlockedDay;
      const best = snapshot.bestResults[day.id];
      const footer = unlocked ? (best ? `${'★'.repeat(best.stars)}${'☆'.repeat(3 - best.stars)}` : day.title) : day.title;
      return `<button class="day-card ${unlocked ? 'unlocked' : 'locked'}" data-action="select-day" data-day="${day.id}" ${unlocked ? '' : 'disabled'}>
        <span>${unlocked ? 'GÜN' : 'KİLİTLİ'}</span><strong>${day.id}</strong><small>${footer}</small>
      </button>`;
    };
    return [0, 5, 10, 15].map((start) => `<section class="day-group"><h3>GÜN ${start + 1}–${start + 5}</h3><div>${DAYS.slice(start, start + 5).map(card).join('')}</div></section>`).join('');
  }

  private renderUpgradeCards(snapshot: SaveData): string {
    return UPGRADES.map((upgrade) => {
      const level = snapshot.upgrades[upgrade.id] ?? 0;
      const locked = snapshot.unlockedDay < upgrade.unlockDay;
      const maxed = level >= upgrade.maxLevel;
      const price = upgradePrice(upgrade, level);
      const disabled = locked || maxed || snapshot.cafeMoney < price;
      const buttonLabel = locked ? `GÜN ${upgrade.unlockDay}` : maxed ? 'TAMAM' : `₺${price} · SATIN AL`;
      return `<article class="upgrade-card ${locked ? 'is-locked' : ''}">
        <span class="upgrade-icon">${upgrade.icon}</span>
        <div class="upgrade-copy"><small>${upgrade.category}</small><strong>${upgrade.name}</strong><p>${upgrade.effect}</p><i>SEVİYE ${level}/${upgrade.maxLevel}</i></div>
        <button data-action="buy-upgrade" data-upgrade="${upgrade.id}" ${disabled ? 'disabled' : ''}>${buttonLabel}</button>
      </article>`;
    }).join('');
  }

  private bindEvents(): void {
    this.root.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-action]');
      if (!button || button.disabled) return;
      this.audio.unlock();
      const action = button.dataset.action;
      if (action === 'select-day') {
        this.openDayStart(Number(button.dataset.day ?? 1));
        return;
      }
      if (action === 'buy-upgrade') {
        this.purchaseUpgrade(button.dataset.upgrade as UpgradeId);
        return;
      }
      const handlers: Record<string, () => void> = {
        'new-game': () => this.openDayStart(1),
        continue: () => this.openDayStart(this.save.snapshot.unlockedDay),
        'day-select': () => { this.refreshProgressionUi(); this.openModal('day-select'); },
        cafe: () => this.openCafe(),
        settings: () => this.openModal('settings'),
        'close-modal': () => this.closeActiveModal(),
        'start-day': () => this.startDay(),
        'select-day': () => this.openModal('day-start'),
        pause: () => this.pauseGame(),
        resume: () => this.resumeGame(),
        'save-settings': () => this.saveSettings(),
        'apply-cheat': () => this.applyCheatCode(),
        replay: () => this.replay(),
        'main-menu': () => this.returnToMenu(),
        'next-day': () => this.goToNextDay(),
      };
      if (action) handlers[action]?.();
    });

    this.root.addEventListener('input', (event) => {
      const input = (event.target as HTMLElement).closest<HTMLInputElement>('input[data-setting]');
      if (input) input.nextElementSibling!.textContent = `${input.value}%`;
    });

    gameEvents.addEventListener('hud', ((event: CustomEvent<HudState>) => this.updateHud(event.detail)) as EventListener);
    gameEvents.addEventListener('toast', ((event: CustomEvent<{ message: string; tone: string }>) => this.showToast(event.detail.message, event.detail.tone)) as EventListener);
    gameEvents.addEventListener('day-complete', ((event: CustomEvent<DayResult>) => this.showResult(event.detail)) as EventListener);
    gameEvents.addEventListener('finale-step', ((event: CustomEvent<{ state: FinaleState; message: string }>) => this.showFinaleStep(event.detail.state, event.detail.message)) as EventListener);
    gameEvents.addEventListener('campaign-complete', ((event: CustomEvent<CampaignStats>) => this.showCampaignComplete(event.detail)) as EventListener);

    if (new URLSearchParams(location.search).get('dev') === '1') this.enableDevPanel();
  }

  private showScreen(screen: 'menu' | 'game'): void {
    this.root.querySelectorAll<HTMLElement>('[data-screen]').forEach((element) => element.classList.toggle('is-active', element.dataset.screen === screen));
  }

  private openModal(name: string): void {
    const layer = this.root.querySelector<HTMLElement>('#modal-layer')!;
    layer.hidden = false;
    layer.querySelectorAll<HTMLElement>('[data-modal]').forEach((modal) => { modal.hidden = modal.dataset.modal !== name; });
  }

  private hideModal(): void {
    const layer = this.root.querySelector<HTMLElement>('#modal-layer')!;
    layer.hidden = true;
    layer.querySelectorAll<HTMLElement>('[data-modal]').forEach((modal) => { modal.hidden = true; });
  }

  private closeActiveModal(): void {
    if (this.presentationState === 'UPGRADE_FROM_RESULT') {
      this.setPresentationState('DAY_RESULT');
      this.openModal('day-complete');
      return;
    }
    if (this.presentationState === 'UPGRADE_FROM_MENU') {
      this.hideModal();
      this.setPresentationState('MENU');
      this.showScreen('menu');
      return;
    }
    if (this.presentationState === 'DAY_BRIEFING') {
      this.returnToMenu();
      return;
    }
    if (this.presentationState === 'PAUSED') {
      this.openModal('pause');
      return;
    }
    if (this.presentationState === 'DAY_RESULT') {
      this.openModal('day-complete');
      return;
    }
    this.hideModal();
  }

  private setPresentationState(state: PresentationState): void {
    this.presentationState = state;
    this.root.dataset.presentationState = state;
  }

  private openDayStart(dayId: number): void {
    this.selectedDay = Math.max(1, Math.min(20, dayId));
    const day = getDay(this.selectedDay);
    this.setText('#day-start-title', `GÜN ${day.id}`);
    this.setText('#day-start-subtitle', day.title);
    this.setText('#day-start-goal', `₺${day.goal}`);
    this.setText('#day-start-challenge', day.challenge);
    const menu = this.root.querySelector<HTMLElement>('#day-start-menu')!;
    menu.innerHTML = `<span>BUGÜNÜN MENÜSÜ</span>${day.menu.map((id) => `<b><i style="--item-color:#${MENU_ITEMS[id].color.toString(16).padStart(6, '0')}">${MENU_ITEMS[id].shortCode}</i>${MENU_ITEMS[id].name}</b>`).join('')}`;
    this.setPresentationState('DAY_BRIEFING');
    this.openModal('day-start');
  }

  private startDay(): void {
    this.audio.setMusicDuck(1);
    this.hideModal();
    this.setPresentationState('GAMEPLAY');
    this.currentResult = undefined;
    this.showScreen('game');
    const gameScreen = this.root.querySelector<HTMLElement>('[data-screen="game"]')!;
    gameScreen.classList.add('is-entering');
    window.setTimeout(() => gameScreen.classList.remove('is-entering'), 780);
    const parent = this.root.querySelector<HTMLElement>('#phaser-root')!;
    window.__littleCornerDebug = undefined;
    this.game?.destroy(true);
    parent.innerHTML = '';
    this.game = createGame(parent, this.audio, this.save, this.selectedDay);
  }

  private replay(): void {
    this.currentResult = undefined;
    this.startDay();
  }

  private returnToMenu(): void {
    this.audio.setMusicDuck(1);
    this.game?.destroy(true);
    this.game = undefined;
    window.__littleCornerDebug = undefined;
    this.hideModal();
    this.showScreen('menu');
    this.setPresentationState('MENU');
  }

  private updateHud(hud: HudState): void {
    this.setText('#hud-day', String(hud.day));
    this.setText('#hud-earnings', `₺${hud.earnings}`);
    this.setText('#hud-goal', `₺${hud.goal}`);
    this.setText('#hud-combo', `×${hud.combo}`);
    const progress = this.root.querySelector<HTMLElement>('#goal-progress')!;
    progress.style.width = `${Math.min(100, (hud.earnings / hud.goal) * 100)}%`;
    this.root.querySelector('#combo-pill')?.classList.toggle('is-hot', hud.combo >= 2);
    const hintBox = this.root.querySelector<HTMLElement>('#game-hint');
    const hint = this.root.querySelector<HTMLElement>('#game-hint span:last-child');
    if (hint && hintBox) {
      hint.textContent = hud.hint;
      hintBox.classList.add('is-visible');
      if (this.hintTimeout) window.clearTimeout(this.hintTimeout);
      this.hintTimeout = window.setTimeout(() => hintBox.classList.remove('is-visible'), 4200);
    }
  }

  private showToast(message: string, tone: string): void {
    const stack = this.root.querySelector<HTMLElement>('#toast-stack')!;
    const toast = document.createElement('div');
    toast.className = `toast toast-${tone}`;
    toast.textContent = message;
    stack.append(toast);
    window.setTimeout(() => toast.classList.add('is-leaving'), 2200);
    window.setTimeout(() => toast.remove(), 2700);
  }

  private showResult(result: DayResult): void {
    this.currentResult = result;
    this.setText('#result-eyebrow', result.success ? 'HEDEF TAMAMLANDI' : 'HEDEFE ÇOK YAKLAŞTIN');
    this.setText('#result-title', result.success ? 'GÜN TAMAMLANDI' : 'GÜN BİTTİ');
    this.setText('#result-stars', [0, 1, 2].map((index) => index < result.stars ? '★' : '☆').join(' '));
    this.setText('#result-earnings', `₺${result.earnings}`);
    this.setText('#result-tips', `₺${result.tips}`);
    this.setText('#result-served', String(result.served));
    this.setText('#result-lost', String(result.lost));
    this.setText('#result-combo', `×${result.maxCombo}`);
    this.setText('#result-wallet', `₺${this.save.snapshot.cafeMoney}`);
    const bonus = this.root.querySelector<HTMLElement>('#result-bonus')!;
    bonus.hidden = result.bonus === 0;
    bonus.textContent = `✦ Gün bonusu +₺${result.bonus}`;
    const next = this.root.querySelector<HTMLButtonElement>('#result-next')!;
    next.textContent = result.success ? (result.day < 20 ? 'SONRAKİ GÜN' : 'GÜN SEÇ') : 'YENİDEN DENE';
    next.dataset.action = result.success ? (result.day < 20 ? 'next-day' : 'day-select') : 'replay';
    this.setPresentationState('DAY_RESULT');
    this.openModal('day-complete');
  }

  private openCafe(): void {
    const fromResult = this.presentationState === 'DAY_RESULT' || this.presentationState === 'UPGRADE_FROM_RESULT';
    this.setPresentationState(fromResult ? 'UPGRADE_FROM_RESULT' : 'UPGRADE_FROM_MENU');
    this.refreshCafeShop();
    this.openModal('cafe');
  }

  private refreshCafeShop(): void {
    const snapshot = this.save.snapshot;
    this.setText('#cafe-wallet', `₺${snapshot.cafeMoney}`);
    const shop = this.root.querySelector<HTMLElement>('#upgrade-shop');
    if (shop) shop.innerHTML = this.renderUpgradeCards(snapshot);
  }

  private purchaseUpgrade(id: UpgradeId): void {
    const result = this.save.purchaseUpgrade(id);
    if (result.ok) {
      this.audio.play('upgrade');
      this.showToast('Yükseltme kafene eklendi.', 'good');
    } else if (result.reason === 'money') {
      this.showToast(`Bu yükseltme için ₺${result.price} gerekiyor.`, 'warning');
    }
    this.refreshCafeShop();
  }

  private applyCheatCode(): void {
    const input = this.root.querySelector<HTMLInputElement>('#cheat-code');
    const status = this.root.querySelector<HTMLElement>('#cheat-code-status');
    if (!input || !status) return;
    if (input.value.trim().toLocaleLowerCase('tr-TR') !== 'yasemin') {
      status.textContent = 'Kod geçersiz.';
      status.dataset.tone = 'error';
      return;
    }
    const budget = this.save.activateCheat();
    input.value = '';
    status.textContent = `Tüm günler açıldı · Yükseltme bütçesi ₺${budget}`;
    status.dataset.tone = 'success';
    this.refreshProgressionUi();
    this.refreshCafeShop();
    this.audio.play('success');
    this.showToast('Hile kodu etkinleştirildi!', 'good');
  }

  private goToNextDay(): void {
    const nextDay = Math.min(20, (this.currentResult?.day ?? this.selectedDay) + 1);
    this.openDayStart(nextDay);
  }

  private refreshProgressionUi(): void {
    const snapshot = this.save.snapshot;
    const grid = this.root.querySelector<HTMLElement>('.day-grid');
    if (grid) grid.innerHTML = this.renderDayCards(snapshot);
    const continueButton = this.root.querySelector<HTMLButtonElement>('[data-action="continue"]');
    if (continueButton) continueButton.disabled = Object.keys(snapshot.bestResults).length === 0;
    const shop = this.root.querySelector<HTMLElement>('#upgrade-shop');
    if (shop) shop.innerHTML = this.renderUpgradeCards(snapshot);
  }

  private pauseGame(): void {
    this.game?.scene.pause('CafeScene');
    this.setPresentationState('PAUSED');
    this.openModal('pause');
  }

  private resumeGame(): void {
    this.hideModal();
    this.game?.scene.resume('CafeScene');
    this.setPresentationState('GAMEPLAY');
  }

  private saveSettings(): void {
    const settings = {} as SaveData['settings'];
    this.root.querySelectorAll<HTMLInputElement>('input[data-setting]').forEach((input) => {
      settings[input.dataset.setting as keyof SaveData['settings']] = Number(input.value) / 100;
    });
    this.save.updateSettings(settings);
    this.audio.setSettings(settings);
    this.audio.play('coin');
    this.hideModal();
    if (this.game?.scene.isPaused('CafeScene')) this.game.scene.resume('CafeScene');
    this.setPresentationState(this.game ? 'GAMEPLAY' : 'MENU');
    this.showToast('Ayarlar kaydedildi.', 'good');
  }

  private showNotice(title: string, copy: string): void {
    this.setText('#notice-title', title);
    this.setText('#notice-copy', copy);
    this.openModal('notice');
  }

  private enableDevPanel(): void {
    const panel = this.root.querySelector<HTMLElement>('#dev-panel')!;
    panel.hidden = false;
    panel.addEventListener('click', (event) => {
      const dayButton = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-dev-day]');
      if (dayButton) {
        this.selectedDay = Number(dayButton.dataset.devDay ?? 1);
        this.startDay();
        return;
      }
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-dev]');
      if (!button) return;
      const debug = window.__littleCornerDebug;
      const noSceneRequired = ['reset', 'cafe-money', 'unlock', 'reset-upgrades'];
      if (!debug && !noSceneRequired.includes(button.dataset.dev ?? '')) {
        this.setText('#dev-output', 'Önce günü başlat.');
        return;
      }
      if (button.dataset.dev === 'money') debug?.addMoney(25);
      if (button.dataset.dev === 'cafe-money') {
        this.save.addMoney(500);
        this.setText('#dev-output', `Kafe parası: ₺${this.save.snapshot.cafeMoney}`);
        this.refreshProgressionUi();
      }
      if (button.dataset.dev === 'spawn') debug?.spawnCustomer();
      if (button.dataset.dev === 'unlock') {
        this.save.unlockAllDays();
        this.refreshProgressionUi();
        this.setText('#dev-output', 'Gün 1–20 açıldı.');
      }
      if (button.dataset.dev === 'patience') debug?.lowerPatience();
      if (button.dataset.dev === 'ready') debug?.forceFoodReady();
      if (button.dataset.dev === 'gorkem') debug?.triggerGorkem();
      if (button.dataset.dev === 'fevzi') debug?.toggleFevzi();
      if (button.dataset.dev === 'helper') debug?.startHelperAction();
      if (button.dataset.dev === 'finale') debug?.triggerFinale();
      if (button.dataset.dev === 'hitboxes') {
        this.devHitboxes = !this.devHitboxes;
        debug?.setHitboxesVisible(this.devHitboxes);
        button.textContent = this.devHitboxes ? 'HITBOXLARI GİZLE' : 'HITBOXLARI GÖSTER';
      }
      if (button.dataset.dev === 'speed') {
        this.devSpeed = this.devSpeed === 1 ? 2 : this.devSpeed === 2 ? 4 : 1;
        debug?.setSpeed(this.devSpeed);
        button.textContent = `Hız ×${this.devSpeed}`;
      }
      if (button.dataset.dev === 'inspect') this.setText('#dev-output', JSON.stringify(debug?.inspect(), null, 2));
      if (button.dataset.dev === 'reset-upgrades') {
        this.save.resetUpgrades();
        this.refreshProgressionUi();
        this.setText('#dev-output', 'Yükseltmeler sıfırlandı.');
      }
      if (button.dataset.dev === 'reset') {
        this.save.reset();
        location.reload();
      }
    });
  }

  private setText(selector: string, text: string): void {
    const element = this.root.querySelector<HTMLElement>(selector);
    if (element) element.textContent = text;
  }

  private showFinaleStep(state: FinaleState, message: string): void {
    this.audio.setMusicDuck(state === 'INACTIVE' ? 1 : 0.48);
    const caption = this.root.querySelector<HTMLElement>('#finale-caption');
    const text = caption?.querySelector<HTMLElement>('strong');
    if (!caption || !text) return;
    this.root.querySelector<HTMLElement>('.game-frame')?.classList.add('is-finale');
    caption.hidden = false;
    caption.dataset.state = state;
    text.textContent = message;
    caption.classList.remove('is-visible');
    requestAnimationFrame(() => caption.classList.add('is-visible'));
  }

  private showCampaignComplete(stats: CampaignStats): void {
    this.audio.setMusicDuck(0.62);
    this.setText('#campaign-served', String(stats.totalCustomersServed));
    this.setText('#campaign-earned', `₺${stats.totalCafeMoneyEarned}`);
    this.setText('#campaign-tips', `₺${stats.totalTips}`);
    this.setText('#campaign-combo', `×${stats.bestCombo}`);
    this.setText('#campaign-stars', String(stats.totalStars));
    this.setText('#campaign-upgrades', String(stats.upgradesCompleted));
    this.root.querySelector<HTMLElement>('#finale-caption')?.setAttribute('hidden', '');
    this.root.querySelector<HTMLElement>('.game-frame')?.classList.remove('is-finale');
    this.setPresentationState('GAME_COMPLETE');
    this.openModal('campaign-complete');
  }
}
